import React, { createContext, useContext, useState } from "react";

export const PRESETS = [
  { id: "coral",    name: "Coral",    primary: "rgb(244, 159, 153)", primaryHover: "rgb(226, 85, 102)" },
  { id: "sky",      name: "Sky Blue", primary: "rgb(56, 189, 248)",  primaryHover: "rgb(14, 165, 233)" },
  { id: "sage",     name: "Sage",     primary: "rgb(134, 197, 152)", primaryHover: "rgb(74, 161, 102)" },
  { id: "lavender", name: "Lavender", primary: "rgb(167, 139, 250)", primaryHover: "rgb(124, 92, 222)" },
  { id: "peach",    name: "Peach",    primary: "rgb(251, 191, 115)", primaryHover: "rgb(245, 158, 11)" },
  { id: "rose",     name: "Rose",     primary: "rgb(251, 113, 133)", primaryHover: "rgb(225, 29, 72)" },
  { id: "slate",    name: "Slate",    primary: "rgb(148, 163, 184)", primaryHover: "rgb(100, 116, 139)" },
];

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

function darken(rgbStr, amount) {
  const m = rgbStr.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!m) return rgbStr;
  const r = Math.max(0, parseInt(m[1]) - amount);
  const g = Math.max(0, parseInt(m[2]) - amount);
  const b = Math.max(0, parseInt(m[3]) - amount);
  return `rgb(${r}, ${g}, ${b})`;
}

function buildTokens(primary, primaryHover) {
  const m = primary.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!m) return { primary, primaryHover, primaryLight: primary, primarySubtle: primary };
  const [, r, g, b] = m;
  return {
    primary,
    primaryHover,
    primaryLight: `rgba(${r}, ${g}, ${b}, 0.55)`,
    primarySubtle: `rgba(${r}, ${g}, ${b}, 0.12)`,
  };
}

const AppThemeContext = createContext(null);

export function AppThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(
    () => localStorage.getItem("bd_theme_id") || "coral"
  );
  const [customColor, setCustomColor] = useState(
    () => localStorage.getItem("bd_theme_custom") || null
  );

  function selectPreset(id) {
    setThemeId(id);
    localStorage.setItem("bd_theme_id", id);
  }

  function selectCustom(hexColor) {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return;
    const primary = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    const primaryHover = darken(primary, 40);
    setCustomColor(hexColor);
    setThemeId("custom");
    localStorage.setItem("bd_theme_id", "custom");
    localStorage.setItem("bd_theme_custom", hexColor);
    // Store derived primary for custom so tokens work
    _setCustomTokens(buildTokens(primary, primaryHover));
  }

  const [_customTokens, _setCustomTokens] = useState(() => {
    const saved = localStorage.getItem("bd_theme_custom");
    if (saved) {
      const rgb = hexToRgb(saved);
      if (rgb) {
        const primary = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
        return buildTokens(primary, darken(primary, 40));
      }
    }
    return null;
  });

  let tokens;
  if (themeId === "custom" && _customTokens) {
    tokens = _customTokens;
  } else {
    const preset = PRESETS.find(p => p.id === themeId) || PRESETS[0];
    tokens = buildTokens(preset.primary, preset.primaryHover);
  }

  return (
    <AppThemeContext.Provider value={{ tokens, themeId, customColor, selectPreset, selectCustom, PRESETS }}>
      {children}
    </AppThemeContext.Provider>
  );
}

export function useAppTheme() {
  const ctx = useContext(AppThemeContext);
  if (!ctx) throw new Error("useAppTheme must be used inside AppThemeProvider");
  return ctx;
}
